import { ArrowBack } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material';
import { Box, Button, Stack } from '@mui/material';
import type { ReactNode } from 'react';
import { TixlyIconButton } from '../components/button/tixly-icon-button';

// Small buttons below md, medium from md up.
const SIZES = [
  { size: 'small', display: { xs: 'block', md: 'none' } },
  { size: 'medium', display: { xs: 'none', md: 'block' } },
] as const;

export const GoBackHeaderAction = ({
  onClick,
  label,
  backLabel,
  rightAction,
  sx,
}: {
  onClick?: () => void;
  /** Renders a labelled back button instead of the icon-only one. */
  label?: string;
  /** Accessible name of the icon-only back button. */
  backLabel?: string;
  rightAction?: ReactNode;
  sx?: SxProps<Theme>;
}) => {
  // Without an onClick there's nowhere to go back to, so the back button is
  // omitted. A rightAction can still render on its own.
  const backButton =
    onClick &&
    SIZES.map(({ size, display }) => (
      <Box key={size} sx={{ display }}>
        {label ? (
          <Button
            variant="contained"
            color="monochrome"
            startIcon={<ArrowBack />}
            onClick={onClick}
            size={size}
          >
            {label}
          </Button>
        ) : (
          <TixlyIconButton
            variant="contained"
            color="monochrome"
            onClick={onClick}
            aria-label={backLabel}
            size={size}
          >
            <ArrowBack />
          </TixlyIconButton>
        )}
      </Box>
    ));

  return (
    <Stack
      direction="row"
      sx={[
        {
          justifyContent: backButton ? 'space-between' : 'flex-end',
          alignItems: 'center',
          px: { xs: 2.5, md: 3 },
          pt: { xs: 2, md: 3 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {backButton && <Box>{backButton}</Box>}
      <Box>{rightAction}</Box>
    </Stack>
  );
};
